import { useEffect, useRef, useState } from 'react';
import { api } from '../../../../services/api';
import { Janela } from '../../../common/interface';
import { moeda } from '../../formato';
import { mensagemDeErroPix } from '../../usePixCobranca';
import type { PixCobrancaDto } from '../../usePixCobranca';

/** O trajeto que define o valor — calculado no servidor, nunca aqui. */
export interface TrajetoCobertura {
  origemIbge?: string;
  destinoIbge?: string;
  origemNome?: string;
  origemUf?: string;
  destinoNome?: string;
  destinoUf?: string;
  somenteIda: boolean;
  kmDeclarado?: number | null;
  vTPrest?: number | null;
}

/**
 * Os cinco estados de uma cobertura.
 *
 * `LIBERADA` é o **único** que autoriza emitir, e exige o crédito na carteira —
 * não basta a cobrança estar paga. Entre a baixa e o lançamento há uma janela
 * de segundos, e liberar nela faria o cooperado receber "saldo insuficiente"
 * logo depois de um pagamento confirmado, que é o pior erro possível aqui.
 */
type Situacao = 'PENDENTE' | 'LIBERADA' | 'EXPIRADA' | 'CANCELADA' | 'DIVERGENTE';

interface CoberturaDto extends PixCobrancaDto {
  coberturaKey: string;
  situacao: Situacao;
}

const POLL_RAPIDO_MS = 3_000;
const POLL_LENTO_MS = 10_000;
const LIMIAR_LENTO_MS = 60_000;
const TETO_MS = 15 * 60_000;
const MAX_FALHAS_SEGUIDAS = 5;

interface Props {
  aberto: boolean;
  trajeto: TrajetoCobertura | null;
  /** A mensagem do servidor que explica quanto falta. */
  motivo?: string | null;
  onFechar: () => void;
  /** Crédito na carteira: o wizard pode tentar emitir de novo. */
  onLiberada: () => void;
}

/**
 * Pagar a diferença por Pix sem sair do wizard.
 *
 * É o segundo fluxo Pix do painel, e o que faltava desde a 0034: sem ele, o
 * cooperado recusado por saldo tinha de ir à aba Créditos, comprar um pacote
 * inteiro e recomeçar a emissão do zero.
 */
export function CoberturaPixSheet({ aberto, trajeto, motivo, onFechar, onLiberada }: Props) {
  const [cobertura, setCobertura] = useState<CoberturaDto | null>(null);
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tetoAtingido, setTetoAtingido] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const inicioRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // O assistente monta o trajeto como objeto novo a cada redesenho. Com o objeto
  // na dependência, cada redesenho com a janela aberta pedia outra cobrança.
  const chaveTrajeto = trajeto ? JSON.stringify(trajeto) : null;

  useEffect(() => {
    if (!aberto || !trajeto) {
      setCobertura(null); setErro(null); setTetoAtingido(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    setCriando(true);
    setErro(null);
    setTetoAtingido(false);
    api.post<CoberturaDto>('/cooperado/creditos/cobertura', trajeto)
      .then(res => { setCobertura(res.data); inicioRef.current = Date.now(); })
      .catch(e => setErro(mensagemDeErroPix(e)))
      .finally(() => setCriando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, chaveTrajeto]);

  useEffect(() => {
    if (!cobertura || cobertura.situacao !== 'PENDENTE') return;

    let falhas = 0;

    function agendar() {
      const decorrido = Date.now() - inicioRef.current;
      if (decorrido > TETO_MS) { setTetoAtingido(true); return; }
      timerRef.current = setTimeout(consultar, decorrido < LIMIAR_LENTO_MS ? POLL_RAPIDO_MS : POLL_LENTO_MS);
    }

    function consultar() {
      if (Date.now() - inicioRef.current > TETO_MS) { setTetoAtingido(true); return; }
      api.get<CoberturaDto>('/cooperado/creditos/cobertura', {
        params: { coberturaKey: cobertura!.coberturaKey },
      })
        .then(res => {
          falhas = 0;
          setCobertura(res.data);
          // Só LIBERADA devolve o wizard ao caminho de emissão.
          if (res.data.situacao === 'LIBERADA') { onLiberada(); return; }
          if (res.data.situacao !== 'PENDENTE') return;   // EXPIRADA, CANCELADA, DIVERGENTE
          agendar();
        })
        .catch((e: any) => {
          falhas += 1;
          if (falhas >= MAX_FALHAS_SEGUIDAS) { setErro(mensagemDeErroPix(e)); return; }
          agendar();
        });
    }

    timerRef.current = setTimeout(consultar, POLL_RAPIDO_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cobertura?.coberturaKey, cobertura?.situacao]);

  function copiar() {
    if (!cobertura?.pixCopiaECola) return;
    navigator.clipboard?.writeText(cobertura.pixCopiaECola);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  const situacao = cobertura?.situacao;
  const acaoFinal = (rotulo: string, onClick: () => void, primario = false) => (
    <button type="button" className={`co-btn ${primario ? 'co-btn-primario' : 'co-btn-secundario'}`} onClick={onClick}>
      {rotulo}
    </button>
  );

  return (
    <Janela
      aberta={aberto}
      titulo="Falta saldo para esta emissão"
      contexto="Pague a diferença por Pix e continue de onde parou."
      apoio={situacao === 'PENDENTE' ? 'Deixe esta janela aberta: quando o crédito entrar, a emissão continua.' : null}
      erro={erro}
      onFechar={onFechar}
      acoes={
        erro ? acaoFinal('Voltar', onFechar)
          : situacao === 'LIBERADA' ? acaoFinal('Continuar emissão', onLiberada, true)
          : situacao === 'DIVERGENTE' ? acaoFinal('Voltar', onFechar)
          : situacao === 'EXPIRADA' || situacao === 'CANCELADA' ? acaoFinal('Voltar', onFechar, true)
          : undefined
      }
    >
      {motivo && !erro && <p className="co-nota">{motivo}</p>}

      {(criando || (!cobertura && !erro)) && <p className="co-apoio" role="status">Calculando a diferença…</p>}

      {!erro && cobertura && situacao === 'PENDENTE' && (
        <div className="co-pilha">
          <div>
            <span className="co-rotulo">Você paga</span>
            <div className="co-pix-valor">{moeda(cobertura.valor)}</div>
          </div>

          {tetoAtingido && (
            <p className="co-nota co-nota-atencao" role="status">
              Ainda não recebemos a confirmação. Se você já pagou, o crédito entra sozinho:
              feche e tente solicitar de novo em alguns minutos.
            </p>
          )}

          {/* Do base64: `qrCodeUrl` é da superfície mobile e exige x-api-key. */}
          {cobertura.qrCodePngBase64 && (
            <div className="co-pix-qr">
              <img src={`data:image/png;base64,${cobertura.qrCodePngBase64}`} alt="QR Code do Pix" />
            </div>
          )}

          {cobertura.pixCopiaECola && (
            <button type="button" className="co-btn co-btn-primario co-btn-largo" onClick={copiar}>
              {copiado ? 'Código copiado' : 'Copiar código Pix'}
            </button>
          )}
        </div>
      )}

      {!erro && situacao === 'LIBERADA' && (
        <Estado titulo="Crédito liberado" texto="Pode solicitar a ATF agora." />
      )}

      {!erro && situacao === 'DIVERGENTE' && (
        // Pagou a menor: o valor entrou como crédito, mas não cobre a emissão.
        // Dizer "não identificado" o faria pagar tudo de novo.
        <Estado
          titulo="Recebemos um valor menor"
          texto="O que você pagou virou crédito, mas ainda não cobre esta emissão. Feche e tente de novo para ver quanto falta."
        />
      )}

      {!erro && (situacao === 'EXPIRADA' || situacao === 'CANCELADA') && (
        <Estado
          titulo={situacao === 'EXPIRADA' ? 'Este código expirou' : 'Cobrança cancelada'}
          texto="Feche e tente solicitar a ATF de novo para gerar outro."
        />
      )}
    </Janela>
  );
}

function Estado({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="co-desfecho" role="status">
      <strong className="co-titulo-cartao">{titulo}</strong>
      <p className="co-apoio">{texto}</p>
    </div>
  );
}
